import type { BoxProps } from '@withjoy/joykit';
import { Box } from '@withjoy/joykit';
import React, { useMemo } from 'react';
import type { HeaderPhotoSchemaByWebsiteLayout } from '@shared/types/WebsiteHeaderSettings.types';
import { getHeaderPhotoAspectRatio } from '@apps/guest/packages/layout-engine/layouts/layout.utils';
import globalWindow from '@shared/core/globals';
import { OptimizedEventGuestSitePhoto } from '@apps/guest/packages/layout-engine/components/OptimizedEventGuestSitePhoto';

interface FullBleedImageProps {
  photoDetails: HeaderPhotoSchemaByWebsiteLayout['brannan']['fullWidth'] | HeaderPhotoSchemaByWebsiteLayout['brannan']['pageWidth'];
  variant: 'screen' | 'page';
  showPlaceholder?: boolean;
}

const FULL_BLEED_IMAGE_PROPERTIES: Record<'parent', BoxProps> = {
  parent: {
    display: 'flex',
    alignItems: 'center',
    maxHeight: '600px',
    position: 'relative',
    width: '100vw',
    overflow: 'hidden',
    // All CSS units are converted to values before doing the operation.
    // -50vw: is 50% of the viewport width
    // +50%: is 50% of the *element's* width
    //
    // it's effectively the same as setting `left: -50%; transform: translateX(50%);`
    // but we need to calculate based off the viewport, not the element's width
    left: 'calc(-50vw + 50%)',
  },
};

const PAGE_WIDTH_IMAGE_PROPERTIES: Record<'parent', BoxProps> = {
  parent: {
    display: 'flex',
    alignItems: 'center',
    maxHeight: '600px',
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
    left: '0px',
  },
};

// Uncropped fallback ARs per variant — getHeaderPhotoAspectRatio defaults to
// 16/9 when no cropInfo, but screen (fullWidth) historically used 21/9.
const UNCROPPED_ASPECT_RATIO = {
  screen: 21 / 9,
  page: 16 / 9,
};

const PageOrScreenWidthImage = ({ photoDetails, variant, showPlaceholder }: FullBleedImageProps) => {
  const photo = photoDetails.photos?.[0];

  const computeAspectRatio = useMemo(() => {
    if (photo?.cropInfo) {
      return getHeaderPhotoAspectRatio(photo.cropInfo, photo);
    }
    return UNCROPPED_ASPECT_RATIO[variant];
  }, [photo, variant]);

  const imageContainerProps = variant === 'page' ? PAGE_WIDTH_IMAGE_PROPERTIES.parent : FULL_BLEED_IMAGE_PROPERTIES.parent;

  if (!photo) {
    return photoDetails.presentationLayout && showPlaceholder ? (
      <Box
        {...imageContainerProps}
        height={600}
        backgroundColor="lightgray"
      />
    ) : null;
  }

  const { cropInfo } = photo;
  // Use CSS aspect-ratio for intrinsic height so it works on SSR
  // (globalWindow.innerWidth is 0 on server, causing layout shift).
  // maxHeight: 600px on imageContainerProps caps it.
  const containerWidth = globalWindow.innerWidth ?? 0;
  const containerHeight = Math.min(containerWidth / computeAspectRatio, 600);

  const normalizedCropInfo = cropInfo ? { x: cropInfo.x, y: cropInfo.y, height: cropInfo.height, width: cropInfo.width, unit: '%' as const } : null;

  // screenWidth + cropped: a flex child sized by the crop's natural AR (via
  // aspectRatio). The parent's alignItems:center vertically centers it, and
  // maxHeight:600px + overflow:hidden clips the excess — same as how the old
  // CropAndRenderImage canvas was a flex child with auto height.
  // No position:absolute or transforms needed.
  if (variant === 'screen' && normalizedCropInfo) {
    return (
      <Box {...imageContainerProps}>
        <Box
          width="100%"
          aspectRatio={`${computeAspectRatio}`}
        >
          <OptimizedEventGuestSitePhoto
            photoUrl={photo.url}
            cropInfo={normalizedCropInfo}
            width={containerWidth}
            height={containerWidth / computeAspectRatio}
            containerType="fixed-aspect"
          />
        </Box>
      </Box>
    );
  }

  // pageWidth or uncropped screenWidth
  return (
    <Box
      aspectRatio={`${computeAspectRatio}`}
      {...imageContainerProps}
    >
      <OptimizedEventGuestSitePhoto
        photoUrl={photo.url}
        cropInfo={normalizedCropInfo}
        width={containerWidth}
        height={containerHeight}
        containerType={variant === 'screen' ? 'viewport' : 'fixed-aspect'}
        containerAspectRatio={variant === 'screen' ? computeAspectRatio : undefined}
      />
    </Box>
  );
};

export default PageOrScreenWidthImage;
